import * as React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Box, Button, Paper } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';
import { jwtDecode } from "jwt-decode";
import PostForm from '../forms/PostForm';
import { useDeletePost } from '../../../hooks/history/useDeletePost';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { isOlderThan24Hours } from '../../../utils/isOlderThan24Hours';
import CommentIcon from '@mui/icons-material/Comment';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function PostsList({ fetchData, history = null, historyEntry = null, posts, label = 'Notes' }) {
    const token = localStorage.getItem("accessToken");
    const userId = jwtDecode(token).id;
    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();

    const [formOpen, setFormOpen] = React.useState(false);
    const [formMode, setFormMode] = React.useState("create");
    const [selectedPost, setSelectedPost] = React.useState(null);

    const handleAdd = () => {
        setSelectedPost(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (post) => {
        if (isOlderThan24Hours(post.createdAt))
        {
            showSnackbar(t('posts.messages.no_edit_delete'), "warning");
            return;
        }
        setSelectedPost(post);
        setFormMode("edit");
        setFormOpen(true);
    };

    const { remove } = useDeletePost(fetchData);

    const handleDelete = async (post) => {
        if (isOlderThan24Hours(post.createdAt))
        {
            showSnackbar(t('posts.messages.no_edit_delete'), "warning");
            return;
        }

        try {
            await remove(post.id);
            showSnackbar(t('posts.messages.delete_success'), "success");
        } catch (err) {
            showSnackbar(t('posts.messages.delete_fail'), "error");
        }
    };

    const onSavePostSuccess = () => {
        fetchData();
    };

    return (
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            borderRadius: 2, 
            // bgcolor: 'background.default' 
          }}
        >
          <Typography variant="h6">{label}</Typography>
          
          {posts.length === 0 ? (
            <Divider variant="fullWidth" component="li">
              <Typography variant="caption" color="text.secondary">
                no notes
              </Typography>
            </Divider>
          ) : null}

          <List sx={{ mb: 1, width: '100%', bgcolor: 'background.paper' }}>
            {posts?.map((post) => (
              <React.Fragment key={post.id}>
                <Divider variant="fullWidth" component="li">
                  <Typography variant="caption" color="text.secondary">
                    {formatToLocalDateTime(post.createdAt)}
                  </Typography>
                </Divider>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    post.user_id === userId ? (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <VerticalHamburgerMenu
                          options={[
                            {
                              id: 1,
                              name: 'Edit',
                              callback: () => handleEdit(post),
                              disabled: !userHasAccess(SUB_MODULES.NOTES, 2)
                            },
                            {
                              id: 2,
                              name: 'Delete',
                              callback: () => handleDelete(post),
                              confirmMessage: t('posts.messages.delete_confirm'),
                              disabled: !userHasAccess(SUB_MODULES.NOTES, 3)
                            },
                          ]}
                        />
                      </Box>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={
                      <>
                        {post.createdAt !== post.updatedAt ? (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'inline' }}
                          >
                            {`(edited ${formatToLocalDateTime(post.updatedAt)})`}
                          </Typography>
                        ) : null}
                        <Typography
                          variant="body1"
                          sx={{ whiteSpace: 'pre-line', maxWidth: '90%' }}
                        >
                          {post.message}
                        </Typography>
                      </>
                    }
                    secondary={
                      <span style={{ display: 'block', textAlign: 'left' }}>
                        {`— ${post.user.name}`}
                      </span>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          <PostForm
            successCallback={onSavePostSuccess}
            history={history}
            historyEntry={historyEntry}
            post={selectedPost}
            mode={formMode}
            open={formOpen}
            onClose={() => setFormOpen(false)}
          />
          <RequirePermission subModuleName={SUB_MODULES.NOTES} minAccessLevel={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleAdd}
              startIcon={<CommentIcon />}
            >
              Add Note
            </Button>
          </RequirePermission>
        </Paper>
    );
}